import { Card, CardContent, Skeleton } from "@tellescope/ui";
import { formatDate, formatCurrency } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { CreditCard, DollarSign } from "lucide-react";
import type { Purchase } from "@tellescope/types-models";
import type { PurchaseListBlock as PurchaseListBlockType } from "../../../shared/schema";
import { MobileList } from "../MobileList";
import { useEnduserPurchases } from "../../hooks/useEnduserPurchases";
import { usePurchasesFilter } from "../../hooks/useItemFilter";

interface PurchaseListBlockProps {
  block: PurchaseListBlockType;
}

interface PurchaseCardProps {
  purchase: Purchase;
}

const isSystemDescription = (description: string): boolean => {
  const lower = description.toLowerCase();
  return lower.includes("webhook") || lower.includes("synced from");
};

function PurchaseCard({ purchase }: PurchaseCardProps) {
  return (
    <Card className="overflow-hidden">
      <CardContent className="px-4">
        <div className="flex gap-4">
          {/* Purchase icon */}
          <div className="flex-shrink-0 text-center">
            <div className="w-14 h-14 rounded-lg bg-primary/10 flex flex-col items-center justify-center">
              <DollarSign className="h-6 w-6 text-primary" />
            </div>
          </div>

          {/* Purchase details */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate">{purchase.title}</h4>

            <div className="flex items-center gap-2 text-sm mt-1">
              <span className="font-semibold text-primary">
                {formatCurrency(purchase.cost.amount, purchase.cost.currency)}
              </span>
            </div>

            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <span>{purchase.processor}</span>
            </div>

            {purchase.description && !isSystemDescription(purchase.description) && (
              <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{purchase.description}</div>
            )}

            <div className="text-xs text-muted-foreground mt-1">{formatDate(new Date(purchase.updatedAt))}</div>
          </div>

          {/* Payment method icon */}
          <div className="flex-shrink-0 flex items-center">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function PurchaseListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i}>
          <CardContent className="px-4">
            <div className="flex gap-4">
              <Skeleton className="w-14 h-14 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function PurchaseListBlock({ block }: PurchaseListBlockProps) {
  const { t } = useTranslation("payments");
  const { itemFilter } = block.props;

  const { purchases: rawPurchases, isLoading: purchasesLoading, error } = useEnduserPurchases();

  // Apply item filter if configured
  const { filteredItems: purchases, isLoading: filterLoading } = usePurchasesFilter({
    items: rawPurchases,
    itemFilter,
  });

  const isLoading = purchasesLoading || filterLoading;

  if (isLoading) {
    return <PurchaseListSkeleton count={block.props.maxItems ?? 5} />;
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-4 text-center text-destructive">{t("Failed to load purchases")}</CardContent>
      </Card>
    );
  }

  return (
    <MobileList
      items={purchases}
      renderItem={(purchase) => <PurchaseCard purchase={purchase} />}
      title={t("Your Purchases")}
      maxItems={block.props.maxItems ?? 5}
      emptyMessage={block.props.emptyMessage ?? t("No purchases")}
    />
  );
}
